import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3 py-2 text-sm',
  lg: 'px-4 py-3 text-base',
};

export default function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  className = '',
  size = 'md',
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={[
          'w-full flex items-center justify-between gap-2 bg-white border rounded-xl text-left transition-all outline-none',
          sizeClasses[size],
          open
            ? 'border-orange-400 ring-1 ring-orange-200'
            : 'border-slate-200 hover:border-slate-300',
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
          selected ? 'text-slate-900' : 'text-slate-400',
        ].join(' ')}
      >
        <span className="truncate">{selected ? selected.label : (placeholder ?? 'Select…')}</span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-slate-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 min-w-full w-max bg-white border border-slate-100 rounded-xl shadow-lg overflow-hidden">
          <ul className="max-h-60 overflow-y-auto p-1">
            {placeholder && (
              <li>
                <button
                  type="button"
                  onClick={() => { onChange(''); setOpen(false); }}
                  className={[
                    'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors',
                    value === '' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:bg-orange-50 hover:text-slate-900',
                  ].join(' ')}
                >
                  {value === '' && <Check className="w-3.5 h-3.5 shrink-0" />}
                  <span className={value === '' ? '' : 'pl-[1.375rem]'}>{placeholder}</span>
                </button>
              </li>
            )}
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => { onChange(opt.value); setOpen(false); }}
                    className={[
                      'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors',
                      isSelected
                        ? 'bg-orange-500 text-white'
                        : 'text-slate-700 hover:bg-orange-50 hover:text-slate-900',
                    ].join(' ')}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5 shrink-0" />}
                    <span className={isSelected ? '' : 'pl-[1.375rem]'}>{opt.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
